/** @jsx jsx */
import { gql } from '@apollo/client';
import IconError from '@apollo/icons/large/IconError.svg';
import IconSuccess from '@apollo/icons/large/IconSuccess.svg';
import IconErrorSolid from '@apollo/icons/small/IconErrorSolid.svg';
import IconSuccessSolid from '@apollo/icons/small/IconSuccessSolid.svg';
import { Button, Link, customColors } from '@apollo/orbit';
import { List } from '@apollo/space-kit/List';
import { Popover } from '@apollo/space-kit/Popover';
import { css, jsx, keyframes } from '@emotion/react';
import classnames from 'classnames';

import { CheckWorkflowTaskItemFragment } from 'src/app/graph/checksPage/checkWorkflowsListPage/checkWorkflowItem/checkWorkflowTaskItem/CheckWorkflowTaskItem';
import { GraphRef } from 'src/app/graph/hooks/useGraphRef';
import {
  legacyFilterBuildCheckDetailsRoute,
  legacyOperationsCheckDetailsRoute,
} from 'src/app/graph/routes';
import { WrapMaybe } from 'src/components/wrapMaybe/WrapMaybe';
import { absoluteURL } from 'src/lib/absoluteURL';
import { assertUnreachable } from 'src/lib/assertUnreachable';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import {
  CheckWorkflowStatus,
  CheckWorkflowTaskStatus,
} from 'src/lib/graphqlTypes/types';

export const CheckWorkflowStatusWidgetFragment = gql`
  fragment CheckWorkflowStatusWidgetFragment on CheckWorkflow {
    id
    graph {
      id
    }
    baseVariant {
      id
      graphId
      name
    }
    status
    buildTask {
      id
      ...CheckWorkflowTaskItemFragment
    }
    operationsTask {
      id
      result {
        id
        createdAt
        numberOfCheckedOperations
      }
      ...CheckWorkflowTaskItemFragment
    }
  }
  ${CheckWorkflowTaskItemFragment}
`;

export const CheckWorkflowStatusWidget = ({
  checkWorkflow,
  className,
}: {
  checkWorkflow: GraphQLTypes.CheckWorkflowStatusWidgetFragment;
  className?: string;
}) => {
  if (!checkWorkflow.baseVariant) {
    throw new Error(
      'Cannot render checks widget for check workflow with no base variant.',
    );
  }
  const graphRef: GraphRef = {
    graphId: checkWorkflow.graph.id,
    graphVariant: checkWorkflow.baseVariant.name,
  };
  return (
    <div className={classnames('flex items-center', className)}>
      <Popover
        placement="bottom-end"
        content={
          <List className="space-y-1">
            {checkWorkflow.buildTask && (
              <TaskItem
                className="pb-1"
                taskName="Build Task"
                taskDescription={
                  checkWorkflow.buildTask.status ===
                  CheckWorkflowTaskStatus.PASSED
                    ? 'Contract schema built succesfully.'
                    : checkWorkflow.buildTask.status ===
                        CheckWorkflowTaskStatus.FAILED
                      ? 'Contract schema failed to build.'
                      : undefined
                }
                task={checkWorkflow.buildTask}
                getTaskLink={() =>
                  checkWorkflow.buildTask?.id
                    ? absoluteURL(
                        legacyFilterBuildCheckDetailsRoute.path({
                          ...graphRef,
                          taskId: checkWorkflow.buildTask.id,
                        }),
                      )
                    : undefined
                }
              />
            )}
            {checkWorkflow.operationsTask && (
              <TaskItem
                className="border-t border-primary pt-1"
                taskName="Operations Task"
                taskDescription={
                  checkWorkflow.operationsTask.status ===
                  CheckWorkflowTaskStatus.PASSED
                    ? 'Your changes will not affect any known operations.'
                    : checkWorkflow.operationsTask.status ===
                        CheckWorkflowTaskStatus.FAILED
                      ? `${checkWorkflow.operationsTask.result?.numberOfAffectedOperations} affected operations out of ${checkWorkflow.operationsTask.result?.numberOfCheckedOperations} checked.`
                      : undefined
                }
                task={checkWorkflow.operationsTask}
                getTaskLink={() =>
                  checkWorkflow.operationsTask?.result?.id
                    ? absoluteURL(
                        legacyOperationsCheckDetailsRoute.path({
                          ...graphRef,
                          taskId: checkWorkflow.operationsTask.result.id,
                        }),
                      )
                    : undefined
                }
              />
            )}
          </List>
        }
        trigger={
          <Button
            size="sm"
            variant="secondary"
            leftIcon={<StatusIcon status={checkWorkflow.status} />}
          >
            Checks
          </Button>
        }
      />
    </div>
  );
};

function TaskItem({
  taskName,
  taskDescription,
  task,
  className,
  getTaskLink,
}: {
  taskName: string;
  taskDescription?: string | undefined;
  task: GraphQLTypes.CheckWorkflowTaskItemFragment;
  getTaskLink?: () => string | undefined;
  className?: string;
}) {
  return (
    <WrapMaybe
      on={
        getTaskLink !== undefined &&
        task.status !== CheckWorkflowTaskStatus.PENDING
      }
      element={<Link isExternal to={(getTaskLink && getTaskLink()) || ''} />}
    >
      <div className={className}>
        <div
          className={classnames('flex rounded px-2 py-1', {
            'hover:cursor-pointer hover:bg-silver-light focus:bg-silver active:bg-silver':
              task.status !== CheckWorkflowTaskStatus.PENDING,
          })}
        >
          {task.status === CheckWorkflowTaskStatus.PASSED ? (
            <IconSuccess className="mr-2 mt-1 text-icon-success" />
          ) : task.status === CheckWorkflowTaskStatus.FAILED ? (
            <IconError className="mr-2 mt-1 text-icon-error" />
          ) : (
            <div className="mr-2 mt-1.5 size-2 rounded-full bg-blilet" />
          )}
          <div>
            <div className="flex text-xs">
              <h1 className="font-semibold">{taskName}</h1>
              <span className="ml-2 text-secondary">
                {task.id.substring(0, 6)}
              </span>
            </div>
            {taskDescription && <p>{taskDescription}</p>}
          </div>
        </div>
      </div>
    </WrapMaybe>
  );
}

function StatusIcon({ status }: { status: GraphQLTypes.CheckWorkflowStatus }) {
  switch (status) {
    case CheckWorkflowStatus.FAILED:
      return <IconErrorSolid style={{ color: customColors.icon.error }} />;
    case CheckWorkflowStatus.PASSED:
      return <IconSuccessSolid style={{ color: customColors.icon.success }} />;
    case CheckWorkflowStatus.PENDING:
      return <LoadingPulse className="mr-1" />;
    default:
      return assertUnreachable(status);
  }
}

export function LoadingPulse({ className }: { className?: string }) {
  const publishingPulse = keyframes`
    0%, 30% {
      transform: scale(1);
      border-width: 1.5px;
      opacity: 1;
    }

    40% {
      border-width: 2px;
    }

    50%, 60% {
      /* opacity: 0.2; */
      border-width: 6px;
    }

    65%, 70%, 100% {
      opacity: 0;
    }

    70%, 100% {
      transform: scale(0.7);
      border-width: 10px;
    }
  `;
  return (
    <div className={classnames(className, 'flex items-center justify-center')}>
      <div
        className="absolute size-4 rounded-full border border-blilet"
        css={css`
          animation: 1s ${publishingPulse} ease-in-out infinite alternate;
        `}
      />
      <div className="size-2 rounded-full bg-blilet" />
    </div>
  );
}
